import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, X, Smartphone, Share, MoreVertical, ExternalLink } from 'lucide-react';
import { setupInstallPrompt, installPWA, isPWA } from '../serviceWorkerRegistration';

// Pages where we should NOT show the install prompt
const EXCLUDED_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/partner-desk', '/faida', '/pay/'];

// Detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Detect if in standalone mode (already installed)
const isInStandaloneMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
};

// Detect in-app browsers (Instagram, Facebook, WhatsApp, etc.)
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  // Check for common in-app browser signatures
  return (
    ua.includes('Instagram') ||
    ua.includes('FBAN') ||  // Facebook
    ua.includes('FBAV') ||  // Facebook
    ua.includes('Twitter') ||
    ua.includes('WhatsApp') ||
    ua.includes('Line') ||
    ua.includes('wv') ||    // Android WebView
    ua.includes('Snapchat') ||
    ua.includes('TikTok')
  );
};

export default function InstallPrompt() {
  const location = useLocation();
  const [canInstall, setCanInstall] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [showInAppBrowserPrompt, setShowInAppBrowserPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Don't render on auth pages
  const isAuthPage = EXCLUDED_PATHS.some(path => location.pathname.startsWith(path));
  
  useEffect(() => {
    // Skip all checks on auth pages
    if (isAuthPage) {
      return;
    }
    // Check if already installed
    if (isPWA() || isInStandaloneMode()) {
      setIsInstalled(true);
      return;
    }

    // Check if previously dismissed
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 3 days
      if (Date.now() - dismissedTime < 3 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    // Check for in-app browser FIRST
    if (isInAppBrowser()) {
      setShowInAppBrowserPrompt(true);
      return;
    }

    // For iOS, show special instructions
    if (isIOS()) {
      setShowIOSPrompt(true);
      return;
    }

    // Setup install prompt listener for Android/Desktop
    setupInstallPrompt((available) => {
      setCanInstall(available);
    });
  }, [isAuthPage]);

  const handleInstall = async () => {
    const installed = await installPWA();
    if (installed) {
      setIsInstalled(true);
      setCanInstall(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed or dismissed OR on auth pages
  if (isInstalled || dismissed || isAuthPage) {
    return null;
  }

  // In-app browser detected - show "Open in Browser" prompt
  if (showInAppBrowserPrompt) {
    return (
      <Card className="fixed top-4 left-4 right-4 z-50 border-orange-500/30 bg-background/95 backdrop-blur-lg shadow-xl animate-in slide-in-from-top-4 duration-300 sm:left-auto sm:right-4 sm:w-96">
        <CardContent className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-orange-500/10">
              <ExternalLink className="w-6 h-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-2 text-orange-600">Open in Browser to Install</h3>
              <p className="text-xs text-muted-foreground mb-3">
                You're viewing this in an app. To install Wekeza-Smart:
              </p>
              <ol className="text-xs text-muted-foreground space-y-2 mb-3">
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-orange-500/10 text-orange-600 text-[10px] font-bold">1</span>
                  <span>Tap the <MoreVertical className="w-4 h-4 inline" /> menu (3 dots)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-orange-500/10 text-orange-600 text-[10px] font-bold">2</span>
                  <span>Tap <strong>"Open in Chrome"</strong> or <strong>"Open in Browser"</strong></span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-orange-500/10 text-orange-600 text-[10px] font-bold">3</span>
                  <span>Then follow the install instructions</span>
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                variant="outline"
                size="sm"
                className="w-full rounded-full border-orange-500/30 text-orange-600 hover:bg-orange-500/10"
              >
                Got it!
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // iOS-specific install instructions
  if (showIOSPrompt) {
    return (
      <Card className="fixed top-4 left-4 right-4 z-50 border-primary/20 bg-background/95 backdrop-blur-lg shadow-xl animate-in slide-in-from-top-4 duration-300 sm:left-auto sm:right-4 sm:w-96">
        <CardContent className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-2">Install Wekeza-Smart</h3>
              <p className="text-xs text-muted-foreground mb-3">
                To install this app on your iPhone/iPad:
              </p>
              <ol className="text-xs text-muted-foreground space-y-2 mb-3">
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">1</span>
                  <span>Tap the <Share className="w-4 h-4 inline text-blue-500" /> Share button below</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">2</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">3</span>
                  <span>Tap <strong>"Add"</strong> in the top right</span>
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                variant="outline"
                size="sm"
                className="w-full rounded-full"
              >
                Got it!
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Android/Desktop install prompt
  if (!canInstall) {
    // Show manual instructions for browsers that don't support beforeinstallprompt
    return (
      <Card className="fixed top-4 left-4 right-4 z-50 border-primary/20 bg-background/95 backdrop-blur-lg shadow-xl animate-in slide-in-from-top-4 duration-300 sm:left-auto sm:right-4 sm:w-96">
        <CardContent className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-2">Install Wekeza-Smart</h3>
              <p className="text-xs text-muted-foreground mb-3">
                To install this app on your phone:
              </p>
              <ol className="text-xs text-muted-foreground space-y-2 mb-3">
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">1</span>
                  <span>Tap the <MoreVertical className="w-4 h-4 inline" /> menu (3 dots) in your browser</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">2</span>
                  <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">3</span>
                  <span>Confirm by tapping <strong>"Install"</strong></span>
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                variant="outline"
                size="sm"
                className="w-full rounded-full"
              >
                Got it!
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fixed top-4 left-4 right-4 z-50 border-primary/20 bg-background/95 backdrop-blur-lg shadow-xl animate-in slide-in-from-top-4 duration-300 sm:left-auto sm:right-4 sm:w-80">
      <CardContent className="p-4">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-primary/10">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1">Install Wekeza-Smart</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Add to your home screen for quick access & offline support
            </p>
            <Button 
              onClick={handleInstall}
              size="sm"
              className="w-full rounded-full"
              data-testid="install-pwa-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
